/**
 * useQuizTest - 퀴즈 테스트 비즈니스 로직 훅
 */
import { useState, useCallback } from 'react'
import type { QuizScript, QuizSolveRequest, SolveResult } from '../../types/quizScript'
import type { QuizSolveResult, QuizData, MultiChoiceQuizData } from '../../types/legacyQuiz'
import type { QuizTestApi } from './types'

interface QuizFetchOptions {
  showAnswer?: boolean
  includeSolveResult?: boolean
}

export function useQuizTest(api: QuizTestApi, useFixedVars = true) {
  // Input state
  const [quizId, setQuizId] = useState('')
  const [showAnswer, setShowAnswer] = useState(false)
  const [useFixedVarsState, setUseFixedVars] = useState(useFixedVars)
  const [varsJson, setVarsJson] = useState('')
  const [varsError, setVarsError] = useState<string | null>(null)

  // Quiz state
  const [quizData, setQuizData] = useState<QuizScript | null>(null)
  const [solveResult, setSolveResult] = useState<QuizSolveResult | null>(null)
  const [submittedAnswers, setSubmittedAnswers] = useState<string[]>([])

  // Loading/error state
  const [isLoading, setIsLoading] = useState(false)
  const [isSolving, setIsSolving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Debug info
  const [showDebug, setShowDebug] = useState(false)
  const [rawResponse, setRawResponse] = useState<unknown>(null)
  const [rawSolveResponse, setRawSolveResponse] = useState<unknown>(null)

  // Parse vars JSON
  const parseVars = useCallback((): Record<string, unknown> | null => {
    if (!varsJson.trim()) return {}
    try {
      const parsed = JSON.parse(varsJson)
      setVarsError(null)
      return parsed
    } catch {
      return null // caller should set error message
    }
  }, [varsJson])

  // Get legacy quiz data format
  const getLegacyQuizData = useCallback((): QuizData | null => {
    if (!quizData) return null

    const rawData = quizData as unknown as Record<string, unknown>
    const qd = (rawData.quizData as QuizData) || quizData

    return {
      ...qd,
      type: quizData.type || qd.type,
      title: quizData.title || qd.title,
      problem: quizData.problem || qd.problem,
      vars: quizData.vars || qd.vars,
      image: quizData.image || qd.image,
      imageMobile: quizData.imageMobile || qd.imageMobile,
    } as QuizData
  }, [quizData])

  // Fetch quiz
  const handleFetch = useCallback(
    async (overrideQuizId?: string, errorMessage?: string, options?: QuizFetchOptions) => {
      const targetQuizId = overrideQuizId || quizId
      if (!targetQuizId.trim()) {
        setError(errorMessage || 'Please enter a quiz ID')
        return
      }

      const vars = parseVars()
      if (vars === null) {
        setVarsError(errorMessage || 'Invalid JSON')
        return
      }

      setIsLoading(true)
      setError(null)
      setQuizData(null)
      setSolveResult(null)
      setSubmittedAnswers([])
      setRawResponse(null)
      setRawSolveResponse(null)

      if (overrideQuizId) {
        setQuizId(overrideQuizId)
      }

      try {
        const requestShowAnswer = options?.showAnswer ?? showAnswer
        const requestVars = Object.keys(vars).length > 0 ? vars : undefined
        const data = options?.includeSolveResult
          ? await api.getQuiz(
              targetQuizId.trim(),
              requestShowAnswer,
              requestVars,
              useFixedVarsState,
              true,
            )
          : await api.getQuiz(
              targetQuizId.trim(),
              requestShowAnswer,
              requestVars,
              useFixedVarsState,
            )
        setRawResponse(data)
        setQuizData(data)
      } catch (err) {
        console.error('Failed to fetch quiz:', err)
        setError(errorMessage || 'Failed to fetch quiz')
      } finally {
        setIsLoading(false)
      }
    },
    [quizId, showAnswer, useFixedVarsState, parseVars, api]
  )

  // Handle solve
  const handleSolve = useCallback(
    async (
      _quizId: string | number,
      answers: string[],
      quizVars: Record<string, unknown>,
      elapsedTime: number
    ) => {
      if (!quizData || isSolving) return

      setSubmittedAnswers(answers)
      setIsSolving(true)

      const legacyQuiz = getLegacyQuizData()
      if (!legacyQuiz) {
        setIsSolving(false)
        return
      }

      try {
        const userVars = parseVars()
        const finalVars = userVars && Object.keys(userVars).length > 0 ? userVars : quizVars

        // Transform answers based on quiz type
        let transformedAnswers: (string | number | boolean)[] = []

        switch (legacyQuiz.type) {
          case 'fill_blank':
            transformedAnswers = answers
            break

          case 'multi_choice': {
            const quiz = legacyQuiz as MultiChoiceQuizData
            transformedAnswers = answers
              .map((key) => {
                const index = quiz.choices.findIndex((choice) => choice.key === key)
                return index >= 0 ? index + 1 : 0
              })
              .filter((idx) => idx > 0)
            break
          }

          case 'select_text':
            transformedAnswers = answers.map((a) => {
              const num = parseInt(a, 10)
              return isNaN(num) ? 0 : num
            })
            break

          case 'boolean':
            transformedAnswers = answers.map((a) => a === 'true')
            break

          case 'drag_text':
          case 'coloring':
          case 'counting':
          case 'image_select':
          case 'ordering':
          case 'pairing':
            transformedAnswers = answers
            break

          default:
            transformedAnswers = []
        }

        const request: QuizSolveRequest = {
          answers: transformedAnswers,
          vars: finalVars,
          elapsed: elapsedTime,
        }

        const quizIdForSolve = quizData.quizId
        if (!quizIdForSolve) {
          throw new Error('Quiz ID is missing')
        }

        const apiResult: SolveResult = await api.solve(
          String(quizIdForSolve),
          request
        )

        setRawSolveResponse(apiResult)

        const rawApiResult = apiResult as unknown as Record<string, unknown>
        const apiElapsed = (rawApiResult.elapsed as number) ?? elapsedTime

        const result: QuizSolveResult = {
          correct: apiResult.correct,
          corrects: apiResult.corrects.map((c) => {
            if (typeof c === 'boolean') return c
            if (c === 'true' || c === true) return true
            if (c === 'false' || c === false) return false
            return Boolean(c)
          }),
          correctAnswers: (apiResult.correctAnswers ?? []).map((a) => {
            if (typeof a === 'string') return a
            if (typeof a === 'number') return String(a)
            if (typeof a === 'boolean') return String(a)
            return String(a)
          }),
          elapsedTime: apiElapsed,
          description: apiResult.description
            ? {
                text: apiResult.description.text ?? undefined,
                image: (() => {
                  const desc = apiResult.description as Record<string, unknown>
                  const img = desc.image
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                imageMobile: (() => {
                  const desc = apiResult.description as Record<string, unknown>
                  const img = desc.imageMobile
                  if (typeof img === 'string') return { signedUrl: img }
                  if (img && typeof img === 'object') return img as { signedUrl: string }
                  return undefined
                })(),
                desmos: apiResult.description.desmos ?? undefined,
              }
            : undefined,
        }

        setSolveResult(result)
      } catch (err) {
        console.error('Failed to solve quiz:', err)

        const result: QuizSolveResult = {
          correct: false,
          corrects: [],
          correctAnswers: [],
          elapsedTime,
        }
        setSolveResult(result)
      } finally {
        setIsSolving(false)
      }
    },
    [quizData, isSolving, parseVars, getLegacyQuizData, api]
  )

  // Reset
  const handleReset = useCallback(() => {
    setQuizData(null)
    setSolveResult(null)
    setSubmittedAnswers([])
    setError(null)
    setRawResponse(null)
    setRawSolveResponse(null)
  }, [])

  return {
    // State
    quizId,
    setQuizId,
    showAnswer,
    setShowAnswer,
    useFixedVars: useFixedVarsState,
    setUseFixedVars,
    varsJson,
    setVarsJson,
    varsError,
    setVarsError,
    quizData,
    solveResult,
    submittedAnswers,
    isLoading,
    isSolving,
    error,
    showDebug,
    setShowDebug,
    rawResponse,
    rawSolveResponse,

    // Computed
    legacyQuiz: getLegacyQuizData(),

    // Actions
    handleFetch,
    handleSolve,
    handleReset,
  }
}
